import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

// Force UTC: backend timestamps have no timezone suffix, treat as UTC
function toUTC(val) {
  if (!val) return null;
  if (typeof val === 'string' && !val.endsWith('Z') && !val.includes('+')) return parseISO(val + 'Z');
  return typeof val === 'string' ? parseISO(val) : new Date(val);
}

// ─── Class merging ─────────────────────────────────────────────────────────────
export function cn(...inputs) { return twMerge(clsx(inputs)); }

// ─── Date / Time ───────────────────────────────────────────────────────────────
export function fmtDate(val, fmt = 'dd MMM yyyy') {
  if (!val) return '—';
  try { return format(toUTC(val), fmt); }
  catch { return String(val); }
}
// Billing expiry dates are stored as UTC timestamps without a timezone suffix.
// Display them in IST consistently, regardless of the customer's browser timezone.
export function fmtDateIST(val) {
  if (!val) return '—';
  try {
    const date = toUTC(val);
    if (!date || Number.isNaN(date.getTime())) return String(val);
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  } catch { return String(val); }
}
// fmtDateTime and fmtTime always render in IST (+05:30).
// Self-contained: shifts UTC epoch by exactly 5h30m, then reads UTC parts.
export function fmtDateTime(val) {
  if (!val) return '—';
  try {
    const utc = toUTC(val);
    if (!utc || isNaN(utc.getTime())) return String(val);
    const ist = new Date(utc.getTime() + 19800000);
    const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return String(ist.getUTCDate()).padStart(2, '0')
      + ' ' + MON[ist.getUTCMonth()]
      + ' ' + String(ist.getUTCHours()).padStart(2, '0')
      + ':' + String(ist.getUTCMinutes()).padStart(2, '0')
      + ':' + String(ist.getUTCSeconds()).padStart(2, '0');
  } catch { return String(val); }
}

export function fmtTime(val) {
  if (!val) return '—';
  try {
    const utc = toUTC(val);
    if (!utc || isNaN(utc.getTime())) return String(val);
    const ist = new Date(utc.getTime() + 19800000);
    return String(ist.getUTCHours()).padStart(2, '0')
      + ':' + String(ist.getUTCMinutes()).padStart(2, '0')
      + ':' + String(ist.getUTCSeconds()).padStart(2, '0');
  } catch { return String(val); }
}
export function fmtRelative(val) {
  if (!val) return '—';
  try { return formatDistanceToNow(toUTC(val), { addSuffix: true }); }
  catch { return String(val); }
}

// ─── Numbers ───────────────────────────────────────────────────────────────────
export function fmtPrice(n, decimals = 2) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
export function fmtPnl(n) {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${fmtPrice(n)}`;
}

// Fresh idempotency key for a manual action. Prefer the native crypto UUID; fall back to RFC4122 v4.
// Call only at action time (e.g. on confirm/exit click) — never at render time, never reused.
export function genClientRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export function fmtPct(n, d = 2) {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${Number(n).toFixed(d)}%`;
}

// ─── Deployment Status — Full backend lifecycle ────────────────────────────────
// Backend states: CREATED, VALIDATING, READY, ACTIVE, PAUSED, STOPPED, ERROR
// Also accept lowercase aliases (running → ACTIVE)
export const STATUS_META = {
  // Legacy states
  CREATED:     { label: 'CREATED',    color: 'text-info',      bg: 'bg-info/10 border-info/30',      dot: 'bg-info',      pulse: false },
  VALIDATING:  { label: 'VALIDATING', color: 'text-warn',      bg: 'bg-warn/10 border-warn/30',      dot: 'bg-warn',      pulse: true  },
  READY:       { label: 'READY',      color: 'text-bull',      bg: 'bg-bull/10 border-bull/20',      dot: 'bg-bull',      pulse: false },
  ACTIVE:      { label: 'LIVE',       color: 'text-bull',      bg: 'bg-bull/10 border-bull/30',      dot: 'bg-bull',      pulse: true  },
  PAUSED:      { label: 'PAUSED',     color: 'text-warn',      bg: 'bg-warn/10 border-warn/30',      dot: 'bg-warn',      pulse: false },
  STOPPED:     { label: 'STOPPED',    color: 'text-txt-muted', bg: 'bg-bg-hover border-bg-border',   dot: 'bg-txt-muted', pulse: false },
  ERROR:       { label: 'ERROR',      color: 'text-bear',      bg: 'bg-bear/10 border-bear/30',      dot: 'bg-bear',      pulse: true  },
  // Phase 7 lifecycle states
  DRAFT:       { label: 'DRAFT',      color: 'text-txt-muted', bg: 'bg-bg-hover border-bg-border',   dot: 'bg-txt-muted', pulse: false },
  SCHEDULED:   { label: 'SCHEDULED',  color: 'text-info',      bg: 'bg-info/10 border-info/30',      dot: 'bg-info',      pulse: false },
  STARTING:    { label: 'STARTING',   color: 'text-warn',      bg: 'bg-warn/10 border-warn/30',      dot: 'bg-warn',      pulse: true  },
  STOPPING:    { label: 'STOPPING',   color: 'text-warn',      bg: 'bg-warn/10 border-warn/30',      dot: 'bg-warn',      pulse: true  },
  FAILED:      { label: 'FAILED',     color: 'text-bear',      bg: 'bg-bear/10 border-bear/30',      dot: 'bg-bear',      pulse: false },
  DISABLED:    { label: 'DISABLED',   color: 'text-txt-muted', bg: 'bg-bg-hover border-bg-border',   dot: 'bg-txt-muted', pulse: false },
  // lowercase aliases
  running:     null,
  paused:      null,
  stopped:     null,
  error:       null,
  created:     null,
  validating:  null,
  ready:       null,
  active:      null,
};

export function getStatusMeta(depOrStatus) {
  // Accept either a deployment object (prefers deployment_status) or a plain status string
  const status = typeof depOrStatus === 'object'
    ? (depOrStatus?.deployment_status || depOrStatus?.status)
    : depOrStatus;
  if (!status) return STATUS_META.STOPPED;
  const upper = status.toUpperCase();
  // Alias: 'running' → ACTIVE
  const key = upper === 'RUNNING' ? 'ACTIVE' : upper;
  return STATUS_META[key] || STATUS_META.STOPPED;
}

// ─── Signal Direction ──────────────────────────────────────────────────────────
export const SIGNAL_META = {
  BUY:     { label: 'BUY',     color: 'text-bull', bg: 'bg-bull/10 border-bull/30' },
  SELL:    { label: 'SELL',    color: 'text-bear', bg: 'bg-bear/10 border-bear/30' },
  BUY_CE:  { label: 'BUY CE',  color: 'text-bull', bg: 'bg-bull/10 border-bull/30' },
  SELL_CE: { label: 'SELL CE', color: 'text-bear', bg: 'bg-bear/10 border-bear/30' },
  BUY_PE:  { label: 'BUY PE',  color: 'text-info', bg: 'bg-info/10 border-info/30' },
  SELL_PE: { label: 'SELL PE', color: 'text-warn', bg: 'bg-warn/10 border-warn/30' },
};
export function getSignalMeta(dir) {
  return SIGNAL_META[dir?.toUpperCase()] || {
    label: dir || '—', color: 'text-txt-secondary', bg: 'bg-bg-hover border-bg-border',
  };
}

// ─── Misc ──────────────────────────────────────────────────────────────────────
export function symbolShort(symbol) {
  if (!symbol) return symbol;
  const parts = symbol.split(':');
  return (parts.length > 1 ? parts[1] : parts[0]).split('-')[0];
}
export function truncate(str, n = 40) {
  if (!str) return '';
  return str.length > n ? `${str.slice(0, n)}…` : str;
}
export function safeJson(val, fallback = null) {
  if (val == null) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}
